import formatMessageTimestamp from '@orn/shared-web/utils/formatMessageTimestamp';

import type { IConversationMessage } from '../../hooks/useAssistantWebSocket';

const MS_PER_MINUTE = 60_000;

/** A message earns a bubble only if it has visible content or attachments. */
export function isRenderableMessage(message: IConversationMessage): boolean {
  if (message.content.trim().length > 0) {
    return true;
  }
  return (message.attachments?.length ?? 0) > 0;
}

/**
 * Decide which messages get a timestamp separator above their bubble.
 *
 * Returns a map of message id → formatted label for the renderable messages
 * that open a new wall-clock minute. Messages sharing a minute with the one
 * before collapse to the first, so the transcript shows a timestamp at most
 * once per minute. Non-renderable messages are skipped entirely and neither
 * carry nor reset a separator, so the dedup runs unbroken across the
 * thinking-box split the view draws between in-flight and queued turns.
 */
export function buildTimestampLabels(messages: IConversationMessage[], now: Date = new Date()): Map<string, string> {
  const labels = new Map<string, string>();
  let lastMinuteBucket: number | null = null;

  for (const message of messages) {
    if (!isRenderableMessage(message)) {
      continue;
    }
    const bucket = Math.floor(message.timestamp.getTime() / MS_PER_MINUTE);
    if (bucket === lastMinuteBucket) {
      continue;
    }
    lastMinuteBucket = bucket;
    labels.set(message.id, formatMessageTimestamp(message.timestamp, now));
  }

  return labels;
}
